import React, { useMemo } from 'react'
import { Box, Typography, Chip, Stack, Button } from '@mui/material'
import {
  CheckCircle,
  Cancel,
  Warning,
  Speed,
  Link as LinkIcon,
  Download,
} from '@mui/icons-material'
import { UrlVerificationJob } from './types'
import { ExternalUrlsNodesTable } from './ExternalUrlsNodesTable'
import { ExternalUrlsLeavesTable } from './ExternalUrlsLeavesTable'
import { downloadCsv } from '../../utility'
import { useGetTreeById } from '../../reducers/trees_hooks'
import { createIdMap } from '../version_mapping/utility'

/**
 * Renders the results of a URL verification job.
 * Displays summary statistics, invalid URLs by status, and separate tables for nodes and leaves.
 * @param result - The URL verification job result
 * @returns Formatted display of URL verification findings with detailed status information
 */
export function UrlVerificationResponse({
  result,
}: {
  result: UrlVerificationJob
}) {
  const { data, error } = result

  // Extract data before the error check to determine tree_id and version_id
  const tree_id = data?.data?.tree_id
  const version_id = data?.data?.version_id

  // Get the tree and create the ID map for navigation (hooks must be at top level)
  const { tree } = useGetTreeById({
    treeId: tree_id,
    versionId: version_id,
  })

  const idMap = useMemo(() => {
    return tree ? createIdMap(tree) : undefined
  }, [tree])

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? String(error.message)
        : JSON.stringify(error)

    return (
      <Box padding="10px">
        <Box>
          <strong>ERROR:</strong>
        </Box>
        <Box padding="5px">{errorMessage}</Box>
      </Box>
    )
  }

  const { nodes, leaves, verifications, summary, host_counts } = data.data

  const handleDownloadInvalidUrls = () => {
    const invalidUrls = verifications
      .filter((v) => v.status === 'invalid')
      .map((v) => v.url)
    const uniqueUrls = Array.from(new Set(invalidUrls))
    const csvData = uniqueUrls.map((url) => [url])
    downloadCsv('invalid-urls.csv', csvData)
  }

  return (
    <Box>
      <VerificationSummary summary={summary} />
      <VerificationStatusBreakdown verifications={verifications} />
      <HostCountsSummary hostCounts={host_counts} />

      {/* Only show tables if there are invalid URLs */}
      {(nodes.length > 0 || leaves.length > 0) && (
        <>
          <Box padding="10px" display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" color="error">
              Invalid URLs Found
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              onClick={handleDownloadInvalidUrls}
            >
              Download Invalid URLs
            </Button>
          </Box>
          {nodes.length > 0 && (
            <ExternalUrlsNodesTable
              externalUrls={nodes}
              title="Invalid Node URLs"
              idMap={idMap}
            />
          )}
          {leaves.length > 0 && (
            <ExternalUrlsLeavesTable
              externalUrls={leaves}
              title="Invalid Leaf URLs"
              idMap={idMap}
            />
          )}
        </>
      )}

      {nodes.length === 0 && leaves.length === 0 && (
        <Box padding="10px">
          <Typography variant="body1" color="success.main">
            ✓ All URLs are valid! No broken links found.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

function VerificationSummary({
  summary,
}: {
  summary: UrlVerificationJob['data']['data']['summary']
}) {
  const hasPerformanceData =
    summary.fastest_response_ms !== undefined &&
    summary.slowest_response_ms !== undefined &&
    summary.average_response_ms !== undefined

  return (
    <Box padding="10px">
      <Typography variant="h6">Verification Summary</Typography>
      <Stack direction="row" spacing={1} padding="10px 5px">
        <Chip
          icon={<LinkIcon />}
          label={`${summary.unique_urls} URLs Checked`}
          color="default"
          size="small"
        />
        <Chip
          icon={<CheckCircle />}
          label={`${summary.valid} Valid`}
          color="success"
          size="small"
        />
        <Chip
          icon={<Cancel />}
          label={`${summary.invalid} Invalid`}
          color="error"
          size="small"
        />
        {summary.warnings > 0 && (
          <Chip
            icon={<Warning />}
            label={`${summary.warnings} Warnings`}
            color="warning"
            size="small"
          />
        )}
      </Stack>

      {hasPerformanceData && (
        <Box padding="5px">
          <Typography variant="body2" color="text.secondary">
            <Speed fontSize="small" style={{ verticalAlign: 'middle' }} />{' '}
            Response Times: {summary.fastest_response_ms}ms (fastest) |{' '}
            {Math.round(summary.average_response_ms!)}ms (avg) |{' '}
            {summary.slowest_response_ms}ms (slowest)
          </Typography>
        </Box>
      )}
    </Box>
  )
}

function VerificationStatusBreakdown({
  verifications,
}: {
  verifications: UrlVerificationJob['data']['data']['verifications']
}) {
  const invalidUrls = verifications.filter((v) => v.status === 'invalid')
  const warningUrls = verifications.filter((v) => v.status === 'warning')

  if (invalidUrls.length === 0 && warningUrls.length === 0) {
    return null
  }

  return (
    <Box padding="10px">
      <Typography variant="subtitle1" gutterBottom>
        Status Details
      </Typography>

      {invalidUrls.length > 0 && (
        <Box padding="5px">
          <Typography variant="body2" color="error">
            <strong>Invalid URLs ({invalidUrls.length}):</strong>
          </Typography>
          <Box paddingLeft="15px">
            {groupByStatusCode(invalidUrls).map(({ code, count }) => (
              <Box key={code} marginBottom="5px">
                <Typography variant="body2" color="text.secondary">
                  HTTP {code}: {count} URL{count !== 1 ? 's' : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {warningUrls.length > 0 && (
        <Box padding="5px">
          <Typography variant="body2" color="warning.main">
            <strong>Warnings ({warningUrls.length}):</strong>
          </Typography>
          <Box paddingLeft="15px">
            {groupByStatusCode(warningUrls).map(({ code, count }) => (
              <Box key={code} marginBottom="5px">
                <Typography variant="body2" color="text.secondary">
                  HTTP {code}: {count} URL{count !== 1 ? 's' : ''} (Server Error
                  - may be temporary)
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

function groupByStatusCode(
  verifications: UrlVerificationJob['data']['data']['verifications'],
) {
  const grouped = verifications.reduce(
    (acc, v) => {
      const code = v.http_status_code?.toString() || v.error || 'Unknown'
      if (!acc[code]) {
        acc[code] = { code, count: 0, urls: [] }
      }
      acc[code].count++
      acc[code].urls.push(v.url)
      return acc
    },
    {} as Record<string, { code: string; count: number; urls: string[] }>,
  )

  return Object.values(grouped).sort((a, b) => b.count - a.count)
}

function HostCountsSummary({
  hostCounts,
}: {
  hostCounts: Record<string, number>
}) {
  const entries = Object.entries(hostCounts).sort((a, b) => b[1] - a[1])

  if (entries.length === 0) {
    return null
  }

  return (
    <Box padding="10px">
      <Typography variant="subtitle1">Host Distribution</Typography>
      <Box padding="5px">
        <Typography variant="body2" color="text.secondary">
          Total hosts: {entries.length}
        </Typography>
      </Box>
      <Box padding="5px">
        {entries.slice(0, 10).map(([host, count]) => (
          <Box key={host} style={{ marginBottom: '4px' }}>
            <Typography variant="body2">
              <strong>{host}</strong>: {count} URL{count !== 1 ? 's' : ''}
            </Typography>
          </Box>
        ))}
        {entries.length > 10 && (
          <Typography variant="body2" color="text.secondary">
            ...and {entries.length - 10} more hosts
          </Typography>
        )}
      </Box>
    </Box>
  )
}
